import { useEffect, useState } from "react";
import api from "@/lib/api";
import { fmtTL, fmtDate, fmtInt } from "@/lib/format";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/StatusBadge";

export default function SalesReports() {
  const [sales, setSales] = useState(null);
  const [quotes, setQuotes] = useState(null);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  const load = async () => {
    const params = {};
    if (dateFrom) params.date_from = dateFrom;
    if (dateTo) params.date_to = dateTo;
    const [s, q] = await Promise.all([
      api.get("/reports/sales", { params }),
      api.get("/reports/quotes"),
    ]);
    setSales(s.data);
    setQuotes(q.data);
  };
  useEffect(() => { load(); }, []);

  if (!sales || !quotes) return <div className="text-[#6B7280]">Yükleniyor...</div>;

  return (
    <div className="space-y-5">
      <div className="azd-card p-4 lg:p-5">
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <label className="block text-xs font-semibold text-[#111827] mb-1.5">Başlangıç</label>
            <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="w-40" />
          </div>
          <div>
            <label className="block text-xs font-semibold text-[#111827] mb-1.5">Bitiş</label>
            <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="w-40" />
          </div>
          <Button onClick={load} className="bg-[#0B3676] hover:bg-[#08285A] text-white">Filtrele</Button>
        </div>
      </div>

      <div className="grid lg:grid-cols-4 gap-3">
        <div className="azd-card azd-stat-card p-5">
          <div className="text-[11px] uppercase tracking-wider text-[#6B7280] font-bold">Sipariş Sayısı</div>
          <div className="text-2xl font-extrabold text-[#08285A] mt-1">{fmtInt(sales.order_count)}</div>
        </div>
        <div className="azd-card azd-stat-card azd-success p-5">
          <div className="text-[11px] uppercase tracking-wider text-[#6B7280] font-bold">Toplam Satış</div>
          <div className="text-xl font-extrabold text-[#08285A] mt-1">{fmtTL(sales.total_sales)}</div>
        </div>
        <div className="azd-card azd-stat-card p-5">
          <div className="text-[11px] uppercase tracking-wider text-[#6B7280] font-bold">Ödenen</div>
          <div className="text-xl font-extrabold text-[#16A34A] mt-1">{fmtTL(sales.total_paid)}</div>
        </div>
        <div className="azd-card azd-stat-card azd-error p-5">
          <div className="text-[11px] uppercase tracking-wider text-[#6B7280] font-bold">Kalan Tahsilat</div>
          <div className="text-xl font-extrabold text-[#DC2626] mt-1">{fmtTL(sales.remaining)}</div>
        </div>
      </div>

      <div className="azd-card p-5">
        <div className="text-sm font-bold text-[#08285A] mb-3 uppercase tracking-wider">Teklif Performansı</div>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-3">
          <div className="bg-[#F6F8FB] p-4 rounded-lg">
            <div className="text-[11px] uppercase tracking-wider text-[#6B7280] font-bold">Toplam</div>
            <div className="text-xl font-extrabold text-[#08285A]">{fmtInt(quotes.counts.total)}</div>
          </div>
          <div className="bg-[#F6F8FB] p-4 rounded-lg">
            <div className="text-[11px] uppercase tracking-wider text-[#6B7280] font-bold">Onaylanan</div>
            <div className="text-xl font-extrabold text-[#16A34A]">{fmtInt(quotes.counts.approved + quotes.counts.converted)}</div>
          </div>
          <div className="bg-[#F6F8FB] p-4 rounded-lg">
            <div className="text-[11px] uppercase tracking-wider text-[#6B7280] font-bold">Siparişe Dönüşen</div>
            <div className="text-xl font-extrabold text-[#0B3676]">{fmtInt(quotes.counts.converted)}</div>
          </div>
          <div className="bg-[#F6F8FB] p-4 rounded-lg">
            <div className="text-[11px] uppercase tracking-wider text-[#6B7280] font-bold">Dönüşüm Oranı</div>
            <div className="text-xl font-extrabold text-[#08285A]">%{quotes.conversion_rate.toFixed(1)}</div>
          </div>
        </div>
      </div>

      <div className="azd-card p-5">
        <div className="text-sm font-bold text-[#08285A] mb-3 uppercase tracking-wider">
          Sipariş Listesi ({sales.orders.length})
        </div>
        <div className="overflow-x-auto azd-scrollbar">
          <table className="w-full text-sm">
            <thead className="bg-[#F6F8FB] text-[11px] uppercase tracking-wider text-[#6B7280]">
              <tr>
                <th className="text-left px-3 py-2 font-semibold">Sipariş No</th>
                <th className="text-left px-3 py-2 font-semibold">Firma</th>
                <th className="text-left px-3 py-2 font-semibold">Tarih</th>
                <th className="text-left px-3 py-2 font-semibold">Hazırlayan</th>
                <th className="text-right px-3 py-2 font-semibold">Tutar</th>
              </tr>
            </thead>
            <tbody>
              {sales.orders.slice(0, 50).map((o) => (
                <tr key={o.id} className="border-t border-[#E5E7EB]">
                  <td className="px-3 py-2 font-mono text-xs text-[#0B3676]">{o.order_no}</td>
                  <td className="px-3 py-2 font-semibold">{o.company_name}</td>
                  <td className="px-3 py-2 text-xs text-[#6B7280]">{fmtDate(o.order_date)}</td>
                  <td className="px-3 py-2 text-xs text-[#6B7280]">{o.prepared_by_name}</td>
                  <td className="px-3 py-2 text-right font-bold text-[#08285A]">{fmtTL(o.total_amount)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
